import { create } from 'zustand';
import * as api from '../lib/api';
import type { AgentTaskStatus, Task } from '../lib/api';

interface TaskRunState {
  /** The agent currently running this task, keyed by task id. */
  activeByTaskId: Record<string, string>;
  epoch: number;
  setTaskRun: (taskId: string, agentId: string, active: boolean) => void;
  reconcileTask: (task: Task) => Promise<void>;
  clear: () => void;
}

const statusRequests = new Map<string, Promise<AgentTaskStatus>>();

function requestAgentStatus(agentId: string): Promise<AgentTaskStatus> {
  const pending = statusRequests.get(agentId);
  if (pending) return pending;
  const request = api.fetchAgentTaskStatus(agentId).finally(() => statusRequests.delete(agentId));
  statusRequests.set(agentId, request);
  return request;
}

const withoutTask = (runs: Record<string, string>, taskId: string) => {
  const next = { ...runs };
  delete next[taskId];
  return next;
};

export const useTaskRunStore = create<TaskRunState>()((set, get) => ({
  activeByTaskId: {},
  epoch: 0,

  setTaskRun: (taskId, agentId, active) => set((state) => {
    const current = state.activeByTaskId[taskId];
    if (active && current === agentId) return state;
    // A delayed finish from an old assignee must not erase a newer run.
    if (!active && current !== agentId) return state;
    return {
      epoch: state.epoch + 1,
      activeByTaskId: active
        ? { ...state.activeByTaskId, [taskId]: agentId }
        : withoutTask(state.activeByTaskId, taskId),
    };
  }),

  // Reconnect has no replay of the ephemeral lifecycle rail. The durable agent
  // status snapshot is authoritative until a newer live frame or disconnect.
  reconcileTask: async (task) => {
    if (!task.assignee_id) {
      set((state) => ({ epoch: state.epoch + 1, activeByTaskId: withoutTask(state.activeByTaskId, task.id) }));
      return;
    }
    const observedEpoch = get().epoch;
    try {
      const status = await requestAgentStatus(task.assignee_id);
      // A lifecycle frame or disconnect happened while this request was in
      // flight, so this older snapshot must not overwrite fresher state.
      if (get().epoch !== observedEpoch) return;
      const active = status.state === 'busy' && status.last_task_id === task.id;
      set((state) => ({
        epoch: state.epoch + 1,
        activeByTaskId: active
          ? { ...state.activeByTaskId, [task.id]: task.assignee_id! }
          : withoutTask(state.activeByTaskId, task.id),
      }));
    } catch {
      if (get().epoch !== observedEpoch) return;
      // A failed reconciliation must never preserve an unverified animation.
      set((state) => ({ epoch: state.epoch + 1, activeByTaskId: withoutTask(state.activeByTaskId, task.id) }));
    }
  },

  clear: () => set((state) => ({ epoch: state.epoch + 1, activeByTaskId: {} })),
}));
